import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  Shield
} from 'lucide-react';

type LayoutProps = {
  children: ReactNode;
  currentView: string;
  onViewChange: (view: string) => void;
};

type NavItem = {
  name: string;
  icon: typeof LayoutDashboard;
  path: string;
  adminOnly?: boolean;
};

const getNavItems = (t: (key: string) => string): NavItem[] => [
  { name: t('nav.dashboard'), icon: LayoutDashboard, path: 'dashboard' },
  { name: t('nav.ledger'), icon: FileText, path: 'ledger' },
  { name: t('nav.statement'), icon: FileText, path: 'statement' },
  { name: t('nav.reports'), icon: FileText, path: 'reports' },
  { name: t('nav.admin'), icon: Settings, path: 'admin', adminOnly: true },
];

export default function Layout({ children, currentView, onViewChange }: LayoutProps) {
  const { t } = useTranslation();
  const { user, signOut, isAdmin } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = getNavItems(t);
  const filteredNavItems = navItems.filter(item => !item.adminOnly || isAdmin);

  const handleNavClick = (path: string) => {
    onViewChange(path);
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-slate-900 bg-opacity-50 z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <aside className={`
        fixed md:sticky top-0 left-0 h-screen w-64 bg-white border-r border-slate-200
        flex flex-col transition-transform duration-300 z-50
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
                <LayoutDashboard className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-lg font-bold text-slate-800">{t('common.treasurySystem')}</h1>
            </div>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="md:hidden p-1 text-slate-600 hover:bg-slate-100 rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {filteredNavItems.map((item) => (
            <button
              key={item.path}
              onClick={() => handleNavClick(item.path)}
              className={`
                w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium
                transition-all relative group
                ${currentView === item.path
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'text-slate-600 hover:bg-slate-100'
                }
              `}
            >
              {currentView === item.path && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-emerald-600 rounded-r" />
              )}
              <item.icon className="w-5 h-5" />
              {item.name}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-200 space-y-3">
          <div className="px-4 py-2">
            <p className="text-sm font-medium text-slate-800 flex items-center gap-2 mb-1">
              {user?.full_name || user?.email}
              {isAdmin && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded">
                  <Shield className="w-3 h-3" />
                  {t('common.admin')}
                </span>
              )}
            </p>
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          </div>
          <button
            onClick={signOut}
            className="w-full flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <LogOut className="w-5 h-5" />
            {t('nav.signOut')}
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center">
              <LayoutDashboard className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-lg font-bold text-slate-800">{t('common.treasurySystem')}</h1>
          </div>
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg"
          >
            <Menu className="w-6 h-6" />
          </button>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-7xl w-full mx-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
